"use client";

import { useState } from "react";
import { motion } from "motion/react";
import { toast } from "sonner";
import { usePostHog } from "posthog-js/react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Users,
  Heart,
  Mail,
  Phone,
  CheckCircle2,
  Globe,
  Calendar,
} from "lucide-react";

interface SupportContactFormProps {
  inquiryType?: "sponsorship" | "volunteer" | "donation" | "general";
  className?: string;
}

const inquiryTypes = [
  {
    id: "sponsorship",
    title: "Corporate Sponsorship",
    description: "Interested in corporate partnership opportunities",
    icon: Building2,
    color: "bg-secondary/50 border-border",
  },
  {
    id: "volunteer",
    title: "Volunteer Opportunities",
    description: "Want to volunteer your time and skills",
    icon: Users,
    color: "bg-accent/50 border-border",
  },
  {
    id: "donation",
    title: "Make a Donation",
    description: "Ready to support through financial contribution",
    icon: Heart,
    color: "bg-muted/50 border-border",
  },
  {
    id: "general",
    title: "General Inquiry",
    description: "Other questions or information requests",
    icon: Mail,
    color: "bg-muted/30 border-border",
  },
];

const organizationTypes = [
  "Corporation",
  "NGO/Non-profit",
  "Foundation",
  "Government Agency",
  "Educational Institution",
  "Religious Organization",
  "Individual",
  "Other",
];

const volunteerInterests = [
  "English Teaching",
  "Mathematics Tutoring",
  "Science Education",
  "Computer Skills",
  "Arts & Music",
  "Sports Coaching",
  "Life Skills Mentoring",
  "Administrative Support",
  "Fundraising",
  "Website/IT Support",
];

const donationInterests = [
  "Student Scholarships",
  "Infrastructure Development",
  "Educational Programs",
  "Nutrition & Health",
  "Teacher Training",
  "Technology & Equipment",
  "Emergency Support",
  "General Support",
];

export function SupportContactForm({
  inquiryType = "general",
  className,
}: SupportContactFormProps) {
  const posthog = usePostHog();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedInquiryType, setSelectedInquiryType] = useState(inquiryType);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    organizationType: "",
    country: "",
    website: "",
    message: "",
    volunteerType: "",
    availableTime: "",
    skills: [] as string[],
    donationAmount: "",
    donationFrequency: "",
    interests: [] as string[],
    urgency: "",
    newsletter: true,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSkillsChange = (skill: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      skills: checked
        ? [...prev.skills, skill]
        : prev.skills.filter((s) => s !== skill),
    }));
  };

  const handleInterestsChange = (interest: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      interests: checked
        ? [...prev.interests, interest]
        : prev.interests.filter((i) => i !== interest),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact/support", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          inquiryType: selectedInquiryType,
        }),
      });

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || "Failed to send inquiry");
      }

      const result = await response.json();

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        organization: "",
        organizationType: "",
        country: "",
        website: "",
        message: "",
        volunteerType: "",
        availableTime: "",
        skills: [],
        donationAmount: "",
        donationFrequency: "",
        interests: [],
        urgency: "",
        newsletter: true,
      });

      posthog.capture("support_inquiry_submitted", {
        inquiry_type: selectedInquiryType,
        organization_type: formData.organizationType,
        country: formData.country,
        has_website: !!formData.website,
        interest_count: formData.interests.length,
      });

      toast("Inquiry Submitted Successfully");
      setFormSubmitted(true);
    } catch (error) {
      console.error("Error sending inquiry:", error);
      posthog.capture("support_inquiry_failed", {
        error: error instanceof Error ? error.message : "Unknown error",
        inquiry_type: selectedInquiryType,
      });
      toast("Failed to send inquiry");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formSubmitted) {
    return (
      <motion.div
        className="text-center py-8"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", damping: 15 }}
      >
        <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
          <CheckCircle2 className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold mb-4">
          {selectedInquiryType === "volunteer"
            ? "Volunteer Application Submitted!"
            : selectedInquiryType === "donation"
              ? "Thank You for Your Generosity!"
              : selectedInquiryType === "sponsorship"
                ? "Partnership Inquiry Received!"
                : "Inquiry Submitted Successfully!"}
        </h3>
        <p className="text-muted-foreground mb-6">
          Thank you for your interest in supporting Shree Janakalyan Boarding
          School. We have received your {selectedInquiryType} inquiry and will
          get back to you within 24 hours.
        </p>
        <Button onClick={() => setFormSubmitted(false)}>
          Send Another Inquiry
        </Button>
      </motion.div>
    );
  }

  return (
    <div className={className}>
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Get in Touch</CardTitle>
          <CardDescription className="text-center">
            We'd love to hear from you. Choose your inquiry type and we'll
            provide personalized assistance.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Inquiry Type Selection */}
            <div>
              <Label className="text-base font-medium mb-4 block">
                What brings you here today?
              </Label>
              <div className="grid md:grid-cols-2 gap-3">
                {inquiryTypes.map((type) => (
                  <Card
                    key={type.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      selectedInquiryType === type.id
                        ? "ring-2 ring-blue-500 " + type.color
                        : "hover:" + type.color
                    }`}
                    onClick={() => {
                      setSelectedInquiryType(type.id as any);
                      posthog.capture("support_inquiry_type_selected", {
                        type: type.id,
                      });
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          <type.icon className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{type.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {type.description}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <div
                            className={`w-4 h-4 rounded-full border-2 ${
                              selectedInquiryType === type.id
                                ? "bg-primary border-primary"
                                : "border-gray-300"
                            }`}
                          >
                            {selectedInquiryType === type.id && (
                              <div className="w-2 h-2 bg-primary-foreground rounded-full mx-auto mt-0.5" />
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => handleInputChange("country", e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            {/* Organization Information (for sponsorship and some other types) */}
            {(selectedInquiryType === "sponsorship" ||
              selectedInquiryType === "donation") && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="organization">Organization Name</Label>
                    <Input
                      id="organization"
                      value={formData.organization}
                      onChange={(e) =>
                        handleInputChange("organization", e.target.value)
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="organizationType">Organization Type</Label>
                    <Select
                      value={formData.organizationType}
                      onValueChange={(value) =>
                        handleInputChange("organizationType", value)
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {organizationTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="website">Website (optional)</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) =>
                      handleInputChange("website", e.target.value)
                    }
                    placeholder="https://www.example.com"
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {/* Volunteer-specific fields */}
            {selectedInquiryType === "volunteer" && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Preferred Volunteer Type</Label>
                    <RadioGroup
                      value={formData.volunteerType}
                      onValueChange={(value) =>
                        handleInputChange("volunteerType", value)
                      }
                      className="mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="remote" id="remote" />
                        <Label htmlFor="remote">Remote Volunteering</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="onsite" id="onsite" />
                        <Label htmlFor="onsite">On-site Volunteering</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="both" id="both" />
                        <Label htmlFor="both">Open to Both</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div>
                    <Label htmlFor="availableTime">
                      Available Time Commitment
                    </Label>
                    <Select
                      value={formData.availableTime}
                      onValueChange={(value) =>
                        handleInputChange("availableTime", value)
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select commitment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-2hours">
                          1-2 hours per week
                        </SelectItem>
                        <SelectItem value="3-5hours">
                          3-5 hours per week
                        </SelectItem>
                        <SelectItem value="6-10hours">
                          6-10 hours per week
                        </SelectItem>
                        <SelectItem value="parttime">
                          Part-time (20+ hours/week)
                        </SelectItem>
                        <SelectItem value="fulltime">
                          Full-time commitment
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-base font-medium mb-3 block">
                    Areas of Interest/Expertise
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {volunteerInterests.map((skill) => (
                      <div key={skill} className="flex items-center space-x-2">
                        <Checkbox
                          id={skill}
                          checked={formData.skills.includes(skill)}
                          onCheckedChange={(checked) =>
                            handleSkillsChange(skill, checked as boolean)
                          }
                        />
                        <Label htmlFor={skill} className="text-sm">
                          {skill}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Donation-specific fields */}
            {selectedInquiryType === "donation" && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="donationAmount">
                      Intended Donation Amount (USD)
                    </Label>
                    <Select
                      value={formData.donationAmount}
                      onValueChange={(value) =>
                        handleInputChange("donationAmount", value)
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select amount" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">$50</SelectItem>
                        <SelectItem value="100">$100</SelectItem>
                        <SelectItem value="250">$250</SelectItem>
                        <SelectItem value="500">$500</SelectItem>
                        <SelectItem value="1000">$1,000</SelectItem>
                        <SelectItem value="5000">$5,000</SelectItem>
                        <SelectItem value="10000">$10,000+</SelectItem>
                        <SelectItem value="custom">Other amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Donation Frequency</Label>
                    <RadioGroup
                      value={formData.donationFrequency}
                      onValueChange={(value) =>
                        handleInputChange("donationFrequency", value)
                      }
                      className="mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="once" id="once" />
                        <Label htmlFor="once">One-time</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="monthly" id="monthly" />
                        <Label htmlFor="monthly">Monthly</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="annually" id="annually" />
                        <Label htmlFor="annually">Annually</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>

                <div>
                  <Label className="text-base font-medium mb-3 block">
                    Areas You'd Like to Support
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {donationInterests.map((interest) => (
                      <div
                        key={interest}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={interest}
                          checked={formData.interests.includes(interest)}
                          onCheckedChange={(checked) =>
                            handleInterestsChange(interest, checked as boolean)
                          }
                        />
                        <Label htmlFor={interest} className="text-sm">
                          {interest}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Sponsorship-specific fields */}
            {selectedInquiryType === "sponsorship" && (
              <div>
                <Label htmlFor="urgency">Timeline for Partnership</Label>
                <Select
                  value={formData.urgency}
                  onValueChange={(value) => handleInputChange("urgency", value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">
                      Immediate (within 1 month)
                    </SelectItem>
                    <SelectItem value="short">
                      Short-term (2-3 months)
                    </SelectItem>
                    <SelectItem value="medium">
                      Medium-term (3-6 months)
                    </SelectItem>
                    <SelectItem value="long">Long-term (6+ months)</SelectItem>
                    <SelectItem value="exploring">
                      Just exploring options
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Message */}
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => handleInputChange("message", e.target.value)}
                placeholder="Tell us more about your interest in supporting our mission..."
                className="mt-1 min-h-[100px]"
              />
            </div>

            {/* Newsletter signup */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="newsletter"
                checked={formData.newsletter}
                onCheckedChange={(checked) =>
                  handleInputChange("newsletter", checked)
                }
              />
              <Label htmlFor="newsletter" className="text-sm">
                Subscribe to our newsletter for updates on impact and
                opportunities
              </Label>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Inquiry"}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              We'll respond within 24 hours. For urgent matters, call us
              directly.
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
